import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Upload, CheckCircle, AlertCircle, Check, ChevronDown, Globe } from "lucide-react";
import { cn } from "@/lib/utils";
import { useStoreList, getStoreColor } from "@/hooks/useStoreList";
import {
  useCreatePaymentMethod,
  useUpdatePaymentMethod,
  useUploadPaymentQR,
  type PaymentMethod,
} from "./hooks";

type PaymentType = "Bank Transfer" | "Stripe" | "COD" | "PaySolution";

// The store picker (pill dropdown below) binds the `tenant` field directly:
// "" = all storefronts (company-wide, what get_payment_methods treats as the
// default), a storefront_key = show only on that store.

// Thai bank mapping — codes must match EasySlip receiver bank codes.
// Stored as bank code in DB, displayed as Thai name in UI.
const THAI_BANKS = [
  { code: "SCB", th: "ธนาคารไทยพาณิชย์", en: "Siam Commercial Bank" },
  { code: "KBANK", th: "ธนาคารกสิกรไทย", en: "Kasikorn Bank" },
  { code: "KTB", th: "ธนาคารกรุงไทย", en: "Krung Thai Bank" },
  { code: "BBL", th: "ธนาคารกรุงเทพ", en: "Bangkok Bank" },
  { code: "TTB", th: "ธนาคารทหารไทยธนชาต", en: "Tmbthanachart Bank" },
  { code: "GSB", th: "ธนาคารออมสิน", en: "Government Savings Bank" },
  { code: "BAY", th: "ธนาคารกรุงศรีอยุธยา", en: "Bank of Ayudhya" },
  { code: "BAAC", th: "ธนาคารเพื่อการเกษตรและสหกรณ์", en: "Bank for Agriculture and Agricultural Cooperatives" },
  { code: "UOBT", th: "ธนาคารยูโอบี", en: "United Overseas Bank (Thai)" },
  { code: "GHB", th: "ธนาคารอาคารสงเคราะห์", en: "Government Housing Bank" },
  { code: "CIMBT", th: "ธนาคารซีไอเอ็มบีไทย", en: "Cimb Thai Bank" },
  { code: "KKP", th: "ธนาคารเกียรตินาคินภัทร", en: "Kiatnakin Phatra Bank" },
  { code: "LHFG", th: "ธนาคารแลนด์ แอนด์ เฮ้าส์", en: "Land and Houses Bank" },
  { code: "TISCO", th: "ธนาคารทิสโก้", en: "Tisco Bank" },
  { code: "ISBT", th: "ธนาคารอิสลามแห่งประเทศไทย", en: "Islamic Bank of Thailand" },
  { code: "CITI", th: "ธนาคารซิตี้แบงก์", en: "Citi Bank" },
  { code: "HSBC", th: "ธนาคารฮ่องกงและเซี่ยงไฮ้", en: "Hongkong and Shanghai Bank" },
  { code: "ICBCT", th: "ธนาคารไอซีบีซี (ไทย)", en: "Industrial and Commercial Bank of China (Thai)" },
  { code: "PROMPTPAY", th: "พร้อมเพย์", en: "PromptPay" },
] as const;

interface PaymentMethodFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingMethod?: PaymentMethod | null;
}

export function PaymentMethodForm({
  open,
  onOpenChange,
  editingMethod,
}: PaymentMethodFormProps) {
  const isEditing = !!editingMethod;

  // Multi-storefront registry — same source as the dashboard store selector.
  // Single-store tenants (<2) never see the store picker; their methods stay
  // company-wide (blank tenant), which is the correct behavior.
  const { data: stores = [] } = useStoreList();
  const isMultiStore = stores.length >= 2;

  // Form state
  const [paymentType, setPaymentType] = useState<PaymentType>(
    editingMethod?.payment_type ?? "Bank Transfer"
  );
  const [enabled, setEnabled] = useState(editingMethod?.enabled === 1);
  // Which storefront this method shows on. Bound to the `tenant` field —
  // value is a storefront_key; "" = all storefronts (company-wide).
  const [tenant, setTenant] = useState(editingMethod?.tenant ?? "");
  const [storePickerOpen, setStorePickerOpen] = useState(false);
  const [bankName, setBankName] = useState(editingMethod?.bank_name ?? "");
  const [accountName, setAccountName] = useState(editingMethod?.account_name ?? "");
  const [accountNumber, setAccountNumber] = useState(editingMethod?.account_number ?? "");
  const [qrFile, setQrFile] = useState<File | null>(null);
  const [stripePublishableKey, setStripePublishableKey] = useState("");
  const [stripeSecretKey, setStripeSecretKey] = useState("");
  const [stripeWebhookSecret, setStripeWebhookSecret] = useState("");
  const [codMaxAmount, setCodMaxAmount] = useState(
    editingMethod?.cod_max_amount?.toString() ?? ""
  );
  const [psApiKey, setPsApiKey] = useState("");
  const [psSecretKey, setPsSecretKey] = useState("");
  const [psMerchantId, setPsMerchantId] = useState(
    editingMethod?.paysolution_merchant_id ?? ""
  );
  const [psTerminalId, setPsTerminalId] = useState(
    editingMethod?.paysolution_terminal_id ?? ""
  );
  const [psEnvironment, setPsEnvironment] = useState<"Sandbox" | "Production">(
    editingMethod?.paysolution_environment ?? "Sandbox"
  );

  // Mutations
  const createMutation = useCreatePaymentMethod();
  const updateMutation = useUpdatePaymentMethod();
  const uploadQRMutation = useUploadPaymentQR();

  const isPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    uploadQRMutation.isPending;

  const mutationError =
    createMutation.error || updateMutation.error || uploadQRMutation.error;

  const resetForm = useCallback(() => {
    setPaymentType(editingMethod?.payment_type ?? "Bank Transfer");
    setEnabled(editingMethod?.enabled === 1);
    setTenant(editingMethod?.tenant ?? "");
    setBankName(editingMethod?.bank_name ?? "");
    setAccountName(editingMethod?.account_name ?? "");
    setAccountNumber(editingMethod?.account_number ?? "");
    setQrFile(null);
    setStripePublishableKey("");
    setStripeSecretKey("");
    setStripeWebhookSecret("");
    setCodMaxAmount(editingMethod?.cod_max_amount?.toString() ?? "");
    setPsApiKey("");
    setPsSecretKey("");
    setPsMerchantId(editingMethod?.paysolution_merchant_id ?? "");
    setPsTerminalId(editingMethod?.paysolution_terminal_id ?? "");
    setPsEnvironment(editingMethod?.paysolution_environment ?? "Sandbox");
  }, [editingMethod]);

  function validate(): string | null {
    if (paymentType === "Bank Transfer") {
      if (!bankName.trim()) return "Please select a bank";
      if (!accountName.trim()) return "Account name is required";
      if (!accountNumber.trim()) return "Account number is required";
      if (!isEditing && !qrFile) {
        return "QR code image is required";
      }
    }
    if (paymentType === "Stripe" && !isEditing) {
      if (!stripePublishableKey.trim()) return "Stripe publishable key is required";
      if (!stripeSecretKey.trim()) return "Stripe secret key is required";
    }
    if (paymentType === "PaySolution") {
      if (!isEditing) {
        if (!psApiKey.trim()) return "API key is required";
        if (!psSecretKey.trim()) return "Secret key is required";
      }
      if (!psMerchantId.trim()) return "Merchant ID is required";
      if (!psTerminalId.trim()) return "Terminal ID is required";
    }
    return null;
  }

  async function handleSubmit() {
    const error = validate();
    if (error) return;

    if (isEditing && editingMethod) {
      const data: Record<string, any> = {
        enabled: enabled ? 1 : 0,
      };

      // Persist store assignment only for multi-store tenants (the picker is
      // hidden otherwise). "" = show on all storefronts (company-wide).
      if (isMultiStore) data.tenant = tenant;

      if (paymentType === "Bank Transfer") {
        data.bank_name = bankName;
        data.account_name = accountName;
        data.account_number = accountNumber;
      } else if (paymentType === "Stripe") {
        if (stripePublishableKey) data.stripe_publishable_key = stripePublishableKey;
        if (stripeSecretKey) data.stripe_secret_key = stripeSecretKey;
        if (stripeWebhookSecret) data.stripe_webhook_secret = stripeWebhookSecret;
      } else if (paymentType === "COD") {
        data.cod_max_amount = codMaxAmount ? parseFloat(codMaxAmount) : 0;
      } else if (paymentType === "PaySolution") {
        if (psApiKey) data.paysolution_api_key = psApiKey;
        if (psSecretKey) data.paysolution_secret_key = psSecretKey;
        data.paysolution_merchant_id = psMerchantId;
        data.paysolution_terminal_id = psTerminalId;
        data.paysolution_environment = psEnvironment;
      }

      await updateMutation.mutateAsync({ name: editingMethod.name, data });

      if (qrFile) {
        const upload = await uploadQRMutation.mutateAsync({
          docName: editingMethod.name,
          file: qrFile,
        });
        await updateMutation.mutateAsync({
          name: editingMethod.name,
          data: { qr_code_image: upload.file_url },
        });
      }
    } else {
      const data: Record<string, any> = {
        payment_type: paymentType,
        enabled: enabled ? 1 : 0,
      };

      // Persist store assignment only for multi-store tenants (the picker is
      // hidden otherwise). "" = show on all storefronts (company-wide).
      if (isMultiStore) data.tenant = tenant;

      if (paymentType === "Bank Transfer") {
        data.bank_name = bankName;
        data.account_name = accountName;
        data.account_number = accountNumber;
      } else if (paymentType === "Stripe") {
        data.stripe_publishable_key = stripePublishableKey;
        data.stripe_secret_key = stripeSecretKey;
        if (stripeWebhookSecret) data.stripe_webhook_secret = stripeWebhookSecret;
      } else if (paymentType === "COD") {
        if (codMaxAmount) data.cod_max_amount = parseFloat(codMaxAmount);
      } else if (paymentType === "PaySolution") {
        data.paysolution_api_key = psApiKey;
        data.paysolution_secret_key = psSecretKey;
        data.paysolution_merchant_id = psMerchantId;
        data.paysolution_terminal_id = psTerminalId;
        data.paysolution_environment = psEnvironment;
      }

      const created = await createMutation.mutateAsync(data);

      if (qrFile && created?.name) {
        const upload = await uploadQRMutation.mutateAsync({
          docName: created.name,
          file: qrFile,
        });
        await updateMutation.mutateAsync({
          name: created.name,
          data: { qr_code_image: upload.file_url },
        });
      }
    }

    onOpenChange(false);
    resetForm();
  }

  const validationError = validate();

  const hasExistingStripeKeys =
    isEditing && editingMethod?.payment_type === "Stripe";
  const hasExistingPsKeys =
    isEditing && editingMethod?.payment_type === "PaySolution";

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) resetForm();
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-w-lg rounded-2xl">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Edit Payment Method" : "Add Payment Method"}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Update payment method configuration"
              : "Configure a new payment method for your storefront"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Payment Type */}
          <div className="space-y-2">
            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Payment Type
            </Label>
            <Select
              value={paymentType}
              onValueChange={(v) => setPaymentType(v as PaymentType)}
              disabled={isEditing}
            >
              <SelectTrigger className="rounded">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-md">
                <SelectItem value="Bank Transfer">Bank Transfer / PromptPay</SelectItem>
                <SelectItem value="Stripe">Stripe</SelectItem>
                <SelectItem value="COD">Cash on Delivery</SelectItem>
                <SelectItem value="PaySolution">PaySolution</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Enabled toggle */}
          <div className="flex items-center justify-between rounded-md bg-muted/30 p-4">
            <div>
              <p className="text-sm font-medium">Enabled</p>
              <p className="text-xs text-muted-foreground">
                Show this payment method to customers
              </p>
            </div>
            <Switch checked={enabled} onCheckedChange={setEnabled} />
          </div>

          {/* Storefront assignment — only for multi-store tenants.
              Writes storefront_key into `tenant`; "" = all storefronts. */}
          {isMultiStore && (
            <div className="space-y-2">
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                แสดงบนหน้าร้าน / Show on storefront
              </Label>
              {(() => {
                const selected = tenant
                  ? stores.find((s) => s.storefront_key === tenant)
                  : null;
                return (
                  <Popover open={storePickerOpen} onOpenChange={setStorePickerOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        aria-label="Show on storefront"
                        className="flex w-full items-center gap-2.5 rounded-full border border-border/70 bg-background px-4 py-2.5 text-sm shadow-apple-sm transition-colors hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {selected ? (
                          <span
                            className={cn(
                              "h-2.5 w-2.5 shrink-0 rounded-full",
                              getStoreColor(selected.color_index)
                            )}
                          />
                        ) : (
                          <Globe className="h-4 w-4 shrink-0 text-muted-foreground" />
                        )}
                        <span className="truncate font-medium text-foreground/90">
                          {selected ? selected.label : "ทุกหน้าร้าน / All storefronts"}
                        </span>
                        <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-muted-foreground" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      align="start"
                      sideOffset={6}
                      className="w-[--radix-popover-trigger-width] rounded-2xl p-1.5"
                    >
                      {/* All storefronts (blank tenant = company-wide) */}
                      <button
                        type="button"
                        className="flex w-full items-center gap-2.5 rounded-full px-3 py-2.5 text-sm transition-colors hover:bg-muted/50"
                        onClick={() => {
                          setTenant("");
                          setStorePickerOpen(false);
                        }}
                      >
                        <Globe className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className="truncate">ทุกหน้าร้าน / All storefronts</span>
                        {!tenant && (
                          <Check className="ml-auto h-4 w-4 shrink-0 text-primary" />
                        )}
                      </button>
                      {stores.map((store) => (
                        <button
                          key={store.storefront_key}
                          type="button"
                          className="flex w-full items-center gap-2.5 rounded-full px-3 py-2.5 text-sm transition-colors hover:bg-muted/50"
                          onClick={() => {
                            setTenant(store.storefront_key);
                            setStorePickerOpen(false);
                          }}
                        >
                          <span
                            className={cn(
                              "h-2.5 w-2.5 shrink-0 rounded-full",
                              getStoreColor(store.color_index)
                            )}
                          />
                          <span className="truncate">{store.label}</span>
                          {tenant === store.storefront_key && (
                            <Check className="ml-auto h-4 w-4 shrink-0 text-primary" />
                          )}
                        </button>
                      ))}
                    </PopoverContent>
                  </Popover>
                );
              })()}
              <p className="text-xs text-muted-foreground">
                เลือกร้านที่จะแสดงช่องทางชำระเงินนี้ — ค่าเริ่มต้นคือทุกร้าน
              </p>
            </div>
          )}

          {/* Bank Transfer fields */}
          {paymentType === "Bank Transfer" && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  QR Code Image
                </Label>
                {editingMethod?.qr_code_image && !qrFile && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    QR code uploaded
                  </div>
                )}
                <label className="flex cursor-pointer flex-col items-center gap-2 rounded-md border-2 border-dashed border-muted-foreground/20 p-5 transition-colors hover:border-muted-foreground/40 hover:bg-muted/20">
                  <Upload className="h-5 w-5 text-muted-foreground/50" />
                  <span className="text-sm text-muted-foreground">
                    {qrFile ? qrFile.name : "Click to upload QR code"}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => setQrFile(e.target.files?.[0] ?? null)}
                  />
                </label>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Bank Name
                </Label>
                <Select
                  value={bankName}
                  onValueChange={(v) => setBankName(v)}
                >
                  <SelectTrigger className="rounded">
                    <SelectValue placeholder="Select a bank" />
                  </SelectTrigger>
                  <SelectContent className="rounded-md max-h-72">
                    {THAI_BANKS.map((bank) => (
                      <SelectItem key={bank.code} value={bank.code}>
                        {bank.th} ({bank.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Account Name
                </Label>
                <Input
                  value={accountName}
                  onChange={(e) => setAccountName(e.target.value)}
                  placeholder="Account holder name"
                  className="rounded"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Account Number
                </Label>
                <Input
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  placeholder="e.g. 123-4-56789-0"
                  className="rounded"
                />
              </div>
            </div>
          )}

          {/* Stripe fields */}
          {paymentType === "Stripe" && (
            <div className="space-y-4">
              {hasExistingStripeKeys && (
                <div className="rounded-md bg-muted/30 p-4">
                  <p className="text-sm text-muted-foreground">
                    Stripe keys are configured. Enter new values below to update,
                    or leave blank to keep existing keys.
                  </p>
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Publishable Key
                </Label>
                <div className="relative">
                  <Input
                    value={stripePublishableKey}
                    onChange={(e) => setStripePublishableKey(e.target.value)}
                    placeholder={
                      hasExistingStripeKeys
                        ? "Configured — enter new value to update"
                        : "pk_live_..."
                    }
                    className="rounded"
                  />
                  {hasExistingStripeKeys && !stripePublishableKey && (
                    <CheckCircle className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-green-500" />
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Secret Key
                </Label>
                <div className="relative">
                  <Input
                    type="password"
                    value={stripeSecretKey}
                    onChange={(e) => setStripeSecretKey(e.target.value)}
                    placeholder={
                      hasExistingStripeKeys
                        ? "Configured — enter new value to update"
                        : "sk_live_..."
                    }
                    className="rounded"
                  />
                  {hasExistingStripeKeys && !stripeSecretKey && (
                    <CheckCircle className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-green-500" />
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Webhook Secret
                  <span className="ml-1 text-muted-foreground/60 normal-case tracking-normal">(optional)</span>
                </Label>
                <Input
                  type="password"
                  value={stripeWebhookSecret}
                  onChange={(e) => setStripeWebhookSecret(e.target.value)}
                  placeholder={
                    hasExistingStripeKeys
                      ? "Configured — enter new value to update"
                      : "whsec_..."
                  }
                  className="rounded"
                />
              </div>
            </div>
          )}

          {/* COD fields */}
          {paymentType === "COD" && (
            <div className="space-y-2">
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Max Order Amount
                <span className="ml-1 text-muted-foreground/60 normal-case tracking-normal">(optional)</span>
              </Label>
              <Input
                type="number"
                value={codMaxAmount}
                onChange={(e) => setCodMaxAmount(e.target.value)}
                placeholder="Leave empty for no limit"
                min="0"
                step="0.01"
                className="rounded"
              />
              <p className="text-xs text-muted-foreground">
                Orders above this amount won't show COD as an option.
              </p>
            </div>
          )}

          {/* PaySolution fields */}
          {paymentType === "PaySolution" && (
            <div className="space-y-4">
              {hasExistingPsKeys && (
                <div className="rounded-md bg-muted/30 p-4">
                  <p className="text-sm text-muted-foreground">
                    PaySolution keys are configured. Enter new values below to update,
                    or leave blank to keep existing keys.
                  </p>
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  API Key
                </Label>
                <div className="relative">
                  <Input
                    value={psApiKey}
                    onChange={(e) => setPsApiKey(e.target.value)}
                    placeholder={hasExistingPsKeys ? "Configured — enter new value to update" : "Your PaySolution API key"}
                    className="rounded"
                  />
                  {hasExistingPsKeys && !psApiKey && (
                    <CheckCircle className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-green-500" />
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Secret Key
                </Label>
                <div className="relative">
                  <Input
                    type="password"
                    value={psSecretKey}
                    onChange={(e) => setPsSecretKey(e.target.value)}
                    placeholder={hasExistingPsKeys ? "Configured — enter new value to update" : "Your PaySolution secret key"}
                    className="rounded"
                  />
                  {hasExistingPsKeys && !psSecretKey && (
                    <CheckCircle className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-green-500" />
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Merchant ID
                </Label>
                <Input
                  value={psMerchantId}
                  onChange={(e) => setPsMerchantId(e.target.value)}
                  placeholder="Your PaySolution merchant ID"
                  className="rounded"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Terminal ID
                </Label>
                <Input
                  value={psTerminalId}
                  onChange={(e) => setPsTerminalId(e.target.value)}
                  placeholder="Your PaySolution terminal ID"
                  className="rounded"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Environment
                </Label>
                <Select
                  value={psEnvironment}
                  onValueChange={(v) => setPsEnvironment(v as "Sandbox" | "Production")}
                >
                  <SelectTrigger className="rounded">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-md">
                    <SelectItem value="Sandbox">Sandbox (testing)</SelectItem>
                    <SelectItem value="Production">Production (live)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {editingMethod?.paysolution_postback_url && (
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Postback URL
                  </Label>
                  <div className="rounded bg-muted/30 p-3">
                    <p className="break-all text-xs font-mono text-muted-foreground">
                      {editingMethod.paysolution_postback_url}
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Copy this URL to your PaySolution control panel.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Validation error */}
          {validationError && (
            <div className="flex items-center gap-2 rounded bg-red-50 dark:bg-red-950/20 p-3 text-sm text-red-600 dark:text-red-400">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {validationError}
            </div>
          )}

          {/* Mutation error */}
          {mutationError && (
            <div className="flex items-center gap-2 rounded bg-red-50 dark:bg-red-950/20 p-3 text-sm text-red-600 dark:text-red-400">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {mutationError.message || "An error occurred. Please try again."}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
            className="rounded"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isPending || !!validationError}
            className="rounded"
          >
            {isPending
              ? "Saving..."
              : isEditing
                ? "Save Changes"
                : "Add Method"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
